import React, { useCallback, useEffect, useRef, useState } from "react";
import { Brain, RefreshCw, Loader2 } from "lucide-react";
import { TurfTalk } from "@/api/client";
import { buildDiagnosticsQuery, classifyDiagnosticsError, groupInfo, diagValue, diagMs, formatDiagTime, DIAG_UNKNOWN } from "@/lib/aiDiagnostics";

const PAGE_LIMIT = 50;

function Field({ label, value, testid }) {
  const v = diagValue(value);
  const unknown = v === DIAG_UNKNOWN;
  return (
    <span className="inline-flex items-baseline gap-1" data-testid={testid}>
      <span className="text-white/35">{label}</span>
      <span className={unknown ? "text-white/30 italic" : "text-white/80"}>{String(v)}</span>
    </span>
  );
}

function MsField({ label, value, testid }) {
  const text = diagMs(value);
  const unknown = text === DIAG_UNKNOWN;
  return (
    <span className="inline-flex items-baseline gap-1" data-testid={testid}>
      <span className="text-white/35">{label}</span>
      <span className={unknown ? "text-white/30 italic" : "text-white/80"}>{text}</span>
    </span>
  );
}

// Read-only operator view of each room agent's actual fallback reason. No writes,
// no config toggles, no raw message text / PII — only the backend allowlist.
export function AiDiagnosticsPanel({ canView = true, fetcher }) {
  const call = fetcher || TurfTalk.adminAiDiagnostics;
  const [roomInput, setRoomInput] = useState("");
  const [gameInput, setGameInput] = useState("");
  const [applied, setApplied] = useState({ roomId: "", gameId: "" });
  const [entries, setEntries] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const seqRef = useRef(0);

  const fetchPage = useCallback(async ({ reset, before }) => {
    if (!canView) return;
    const seq = ++seqRef.current; // newer requests supersede stale ones
    reset ? setLoading(true) : setPaging(true);
    setError(null);
    try {
      const res = await call(buildDiagnosticsQuery({ roomId: applied.roomId, gameId: applied.gameId, limit: PAGE_LIMIT, before }));
      if (seq !== seqRef.current) return; // a newer filter/refresh won — drop this result
      const rows = Array.isArray(res && res.entries) ? res.entries : [];
      setEntries((prev) => (reset ? rows : [...prev, ...rows]));
      setNextBefore((res && res.next_before) || null);
      setLoaded(true);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(classifyDiagnosticsError(e));
      if (reset) { setEntries([]); setNextBefore(null); }
      setLoaded(true);
    } finally {
      if (seq === seqRef.current) { setLoading(false); setPaging(false); }
    }
  }, [canView, call, applied]);

  // Any change to the applied filters (initial, Apply, Reset, Refresh) reloads
  // the first page from scratch. The reference change drives this effect.
  useEffect(() => { if (canView) fetchPage({ reset: true, before: undefined }); }, [canView, fetchPage]);

  const applyFilters = () => setApplied({ roomId: roomInput.trim(), gameId: gameInput.trim() });
  const resetFilters = () => { setRoomInput(""); setGameInput(""); setApplied({ roomId: "", gameId: "" }); };
  const refresh = () => setApplied((a) => ({ ...a })); // new ref → first-page reload
  const loadOlder = () => { if (nextBefore && !loading && !paging) fetchPage({ reset: false, before: nextBefore }); };

  if (!canView) {
    return (
      <section className="tt-panel rounded-xl p-4 mb-5" data-testid="ai-diagnostics-panel">
        <p className="text-white/40 text-sm" data-testid="ai-diag-access-denied">Operator access required.</p>
      </section>
    );
  }

  return (
    <section className="tt-panel rounded-xl p-4 mb-5" data-testid="ai-diagnostics-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Brain size={15} className="text-[#4ea152]" /><h2 className="font-display tracking-wide text-white text-sm">AI FALLBACK DIAGNOSTICS · READ-ONLY</h2></div>
        <button onClick={refresh} className="text-white/40 hover:text-white" data-testid="ai-diag-refresh" aria-label="Refresh diagnostics"><RefreshCw size={14} /></button>
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="flex flex-col gap-1 text-[11px] text-white/40">Room ID
          <input value={roomInput} onChange={(e) => setRoomInput(e.target.value)} placeholder="room_id" data-testid="ai-diag-room-input" className="h-9 w-44 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-white/40">Game ID
          <input value={gameInput} onChange={(e) => setGameInput(e.target.value)} placeholder="game_id" data-testid="ai-diag-game-input" className="h-9 w-44 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
        </label>
        <button onClick={applyFilters} data-testid="ai-diag-apply" className="h-9 px-3.5 rounded-lg bg-[#2F7D32] text-white text-sm">Apply</button>
        <button onClick={resetFilters} data-testid="ai-diag-reset" className="h-9 px-3.5 rounded-lg tt-panel text-white/70 hover:text-white text-sm">Reset</button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm py-6" data-testid="ai-diag-loading"><Loader2 size={15} className="animate-spin" /> Loading diagnostics…</div>
      ) : error ? (
        <div className={`text-sm py-4 ${error.kind === "forbidden" || error.kind === "auth" ? "text-[#ff6b60]" : error.kind === "unpublished" ? "text-[#F4B740]" : "text-[#ff6b60]"}`} data-testid="ai-diag-error" data-error-kind={error.kind}>{error.message}</div>
      ) : entries.length === 0 ? (
        <p className="text-white/40 text-sm py-4" data-testid="ai-diag-empty">No diagnostics entries for the current filters.</p>
      ) : (
        <div className="space-y-2" data-testid="ai-diag-list">
          {entries.map((e, i) => {
            const g = groupInfo(e);
            const rowId = diagValue(e.id) === DIAG_UNKNOWN ? `row-${i}` : String(e.id);
            return (
              <div key={rowId} className="rounded-lg border border-[#2A3544] bg-[#10151D] px-3 py-2.5 text-[12px]" data-testid={`ai-diag-row-${rowId}`}>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-white font-medium" data-testid={`ai-diag-role-${rowId}`}>{String(diagValue(e.role))}</span>
                  <Field label="action" value={e.action} testid={`ai-diag-action-${rowId}`} />
                  <Field label="trigger" value={e.trigger} testid={`ai-diag-trigger-${rowId}`} />
                  <Field label="direct" value={typeof e.direct === "boolean" ? String(e.direct) : e.direct} testid={`ai-diag-direct-${rowId}`} />
                  <span className="ml-auto text-white/35" data-testid={`ai-diag-time-${rowId}`}>{formatDiagTime(e.created_at)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                  <Field label="source" value={e.language_source} testid={`ai-diag-langsource-${rowId}`} />
                  <Field label="reason" value={e.language_reason} testid={`ai-diag-langreason-${rowId}`} />
                  <Field label="data" value={e.data_state} testid={`ai-diag-datastate-${rowId}`} />
                  <span className="inline-flex items-baseline gap-1" data-testid={`ai-diag-group-${rowId}`}>
                    <span className="text-white/35">group</span>
                    <span className={g.kind === "group" ? "text-[#7ec4e8]" : "text-white/40 italic"}>{g.kind === "standalone" ? "non-group" : g.label}</span>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                  <Field label="stage" value={e.language_stage} testid={`ai-diag-stage-${rowId}`} />
                  <MsField label="elapsed" value={e.language_elapsed_ms} testid={`ai-diag-elapsed-${rowId}`} />
                  <MsField label="timeout" value={e.language_timeout_ms} testid={`ai-diag-timeout-${rowId}`} />
                  <Field label="err cat" value={e.language_error_category} testid={`ai-diag-errcat-${rowId}`} />
                  <Field label="http" value={e.language_http_status} testid={`ai-diag-http-${rowId}`} />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-white/30">
                  <Field label="id" value={e.id} testid={`ai-diag-id-${rowId}`} />
                  <Field label="room" value={e.room_id} testid={`ai-diag-roomid-${rowId}`} />
                  <Field label="msg" value={e.message_id} testid={`ai-diag-msgid-${rowId}`} />
                  <Field label="game" value={e.source_game_id} testid={`ai-diag-gameid-${rowId}`} />
                </div>
              </div>
            );
          })}
          {nextBefore && (
            <button onClick={loadOlder} disabled={paging} data-testid="ai-diag-load-older" className="w-full h-9 rounded-lg tt-panel text-white/70 hover:text-white text-sm disabled:opacity-40">
              {paging ? "Loading…" : "Load older"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
